import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { Btn, Badge, Input, Spinner } from '../components/shared/UI';
import LanguageSwitcher from '../components/shared/LanguageSwitcher';
import { fmtCurrency, fmtDate, getStatus, balanceDue } from '../utils/helpers';
import { toHtml, hasContent } from '../utils/richText';
import { useT } from '../i18n';
import api from '../utils/api';

const HEX = /^#[0-9a-fA-F]{6}$/;

// What a client sees when they open a share link (/d/<token>). No login, no app chrome.
export default function PublicDocument() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const { t } = useT();
  const [data, setData] = useState(null);
  const [state, setState] = useState('loading'); // loading | ready | missing
  const [name, setName] = useState('');
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');

  const owner = params.get('owner') === '1'; // the sender previewing their own link — not counted as a view

  useEffect(() => {
    // Private correspondence: keep share pages out of search results
    const meta = document.createElement('meta');
    meta.name = 'robots'; meta.content = 'noindex, nofollow';
    document.head.appendChild(meta);
    return () => document.head.removeChild(meta);
  }, []);

  useEffect(() => {
    api.get(`/public/documents/${token}${owner ? '?owner=1' : ''}`)
      .then(r => { setData(r.data); setState('ready'); })
      .catch(() => setState('missing'));
  }, [token, owner]);

  useEffect(() => {
    if (data?.document) document.title = `${data.document.docNumber} — ${data.company.name || 'Invio'}`;
  }, [data]);

  const respond = async action => {
    if (!name.trim()) { setError(t('server.shareNameRequired')); return; }
    if (action === 'reject' && !window.confirm(t('publicDoc.declineConfirm'))) return;
    setBusy(action); setError('');
    try {
      const r = await api.post(`/public/documents/${token}/respond`, { action, name });
      setData(d => ({ ...d, document: r.data.document, canRespond: false }));
    } catch (err) { setError(err.response?.data?.message || t('publicDoc.respondFailed')); }
    finally { setBusy(''); }
  };

  if (state === 'loading') return <Shell><Spinner /><p style={{ textAlign: 'center', color: 'var(--text-muted)' }}>{t('publicDoc.loading')}</p></Shell>;
  if (state === 'missing') return (
    <Shell>
      <div style={{ textAlign: 'center', padding: '64px 16px' }}>
        <div style={{ fontSize: 44, marginBottom: 12 }}>🔗</div>
        <h1 style={{ fontSize: 20, fontWeight: 700, color: 'var(--heading)', marginBottom: 8 }}>{t('publicDoc.notFoundTitle')}</h1>
        <p style={{ fontSize: 14, color: 'var(--text-muted)' }}>{t('publicDoc.notFoundBody')}</p>
      </div>
    </Shell>
  );

  const { document: doc, company, canRespond } = data;
  const head = HEX.test(company.themeHeaderColor) ? company.themeHeaderColor : '#1A3C6E';
  const accent = HEX.test(company.themeAccentColor) ? company.themeAccentColor : '#2E75B6';
  const fmt = n => fmtCurrency(n, doc.currencySymbol || 'RM');
  const st = getStatus(doc.status);
  const client = doc.clientSnapshot || {};
  const noPrices = doc.hidePrices;
  const payable = doc.type === 'invoice';
  const owed = payable ? balanceDue(doc) : 0;
  const pdfUrl = `${api.defaults.baseURL}/public/documents/${token}/pdf`;

  return (
    <Shell>
      {/* Header band in the sender's colours */}
      <div style={{ borderRadius: 14, overflow: 'hidden', display: 'flex', flexWrap: 'wrap', boxShadow: 'var(--shadow-md)' }}>
        <div style={{ flex: '1.5 1 280px', background: head, color: '#fff', padding: '24px 28px', display: 'flex', alignItems: 'center', gap: 16 }}>
          {company.logoUrl && <img src={company.logoUrl} alt="" style={{ height: Math.min(Number(company.logoHeight) || 60, 72), maxWidth: 140, objectFit: 'contain' }} />}
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 22, fontWeight: 800, lineHeight: 1.2 }}>{company.name || '—'}</div>
            <div style={{ fontSize: 12.5, opacity: 0.9, marginTop: 6, lineHeight: 1.6 }}>
              {[company.address, company.city].filter(Boolean).join(', ')}
              {(company.phone || company.email) && <br />}
              {[company.phone, company.email].filter(Boolean).join(' · ')}
            </div>
          </div>
        </div>
        <div style={{ flex: '1 1 220px', background: accent, color: '#fff', padding: '24px 28px', textAlign: 'end' }}>
          <div style={{ fontSize: 22, fontWeight: 900, letterSpacing: 1.5, textTransform: 'uppercase' }}>{t(`docType.${doc.type}`)}</div>
          <div style={{ fontSize: 13, lineHeight: 1.9, marginTop: 4 }}>
            <div>{doc.docNumber}</div>
            <div>{t('publicDoc.issued')}: {fmtDate(doc.issueDate)}</div>
            {doc.type === 'quotation' ? doc.validUntil && <div>{t('publicDoc.validUntil')}: {fmtDate(doc.validUntil)}</div>
              : doc.dueDate && <div>{t('publicDoc.due')}: {fmtDate(doc.dueDate)}</div>}
          </div>
        </div>
      </div>

      {/* Amount due + actions */}
      <div style={{ ...card, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
        <div>
          <Badge label={t(`status.${doc.status}`)} color={st.color} />
          {payable && !noPrices && (
            doc.status === 'paid'
              ? <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--success)', marginTop: 8 }}>✓ {t('publicDoc.paidInFull')}</div>
              : <div style={{ marginTop: 8 }}>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 700 }}>{t('publicDoc.amountDue')}</div>
                  <div style={{ fontSize: 28, fontWeight: 800, color: 'var(--heading)' }}>{fmt(owed)}</div>
                </div>
          )}
        </div>
        <a href={pdfUrl} target="_blank" rel="noreferrer" style={{ textDecoration: 'none' }}>
          <Btn variant="primary" size="lg">⬇ {t('publicDoc.downloadPdf')}</Btn>
        </a>
      </div>

      {doc.einvoice && (
        <div style={{ ...card, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap', borderInlineStart: '4px solid var(--success)' }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--success)' }}>🏛 {t('einv.publicBadge')}</div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2, overflowWrap: 'anywhere' }} dir="ltr">UUID: {doc.einvoice.uuid}</div>
          </div>
          {doc.einvoice.url && <a href={doc.einvoice.url} target="_blank" rel="noreferrer" style={{ textDecoration: 'none' }}><Btn variant="outline">{t('einv.publicVerify')} ↗</Btn></a>}
        </div>
      )}

      {/* Parties */}
      <div style={{ ...card, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 220px), 1fr))', gap: 24 }}>
        <div>
          <p style={label}>{t('publicDoc.from')}</p>
          <p style={{ fontWeight: 700, color: 'var(--heading)' }}>{company.name}</p>
          <p style={detail}>
            {company.regNo && <>{t('publicDoc.regNo')} {company.regNo}<br /></>}
            {company.taxNo && <>{t('publicDoc.taxNo')} {company.taxNo}<br /></>}
            {company.website}
          </p>
        </div>
        <div>
          <p style={label}>{t('publicDoc.to')}</p>
          <p style={{ fontWeight: 700, color: 'var(--heading)' }}>{client.name || '—'}</p>
          <p style={detail}>
            {client.company && <>{client.company}<br /></>}
            {client.email && <>{client.email}<br /></>}
            {client.phone && <>{client.phone}<br /></>}
            {[client.address, client.city].filter(Boolean).join(', ')}
          </p>
        </div>
      </div>

      {/* Items */}
      <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
        {doc.title && <div style={{ padding: '16px 20px 0', fontSize: 16, fontWeight: 700, color: 'var(--heading)' }}>{doc.title}</div>}
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', minWidth: noPrices ? 300 : 520, borderCollapse: 'collapse', marginTop: doc.title ? 12 : 0 }}>
            <thead>
              <tr style={{ background: head }}>
                <th style={{ ...th, textAlign: 'start' }}>{t('doc.description')}</th>
                <th style={{ ...th, textAlign: 'center', width: 70 }}>{t('doc.qty')}</th>
                {!noPrices && <>
                  <th style={{ ...th, textAlign: 'end', width: 130 }}>{t('doc.unitPrice')}</th>
                  <th style={{ ...th, textAlign: 'end', width: 130 }}>{t('doc.amount')}</th>
                </>}
              </tr>
            </thead>
            <tbody>
              {doc.items.map((item, i) => (
                <tr key={i} style={{ borderBottom: '1px solid var(--border-light)' }}>
                  <td style={td} className="pub-rich" dangerouslySetInnerHTML={{ __html: toHtml(item.description) }} />
                  <td style={{ ...td, textAlign: 'center' }}>{item.quantity}</td>
                  {!noPrices && <>
                    <td style={{ ...td, textAlign: 'end' }}>{fmt(item.unitPrice)}</td>
                    <td style={{ ...td, textAlign: 'end', fontWeight: 600 }}>{fmt(item.amount)}</td>
                  </>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!noPrices && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '16px 20px' }}>
            <div style={{ width: '100%', maxWidth: 280 }}>
              {[
                { l: t('doc.subtotal'), v: fmt(doc.subtotal) },
                doc.discountAmount > 0 && { l: `${t('doc.discount')}${doc.discountType === 'percent' ? ` (${doc.discountValue}%)` : ''}`, v: `-${fmt(doc.discountAmount)}` },
                doc.taxRate > 0 && { l: `${t('doc.tax')} (${doc.taxRate}%)`, v: fmt(doc.taxAmount) },
              ].filter(Boolean).map(r => (
                <div key={r.l} style={totalRow}><span>{r.l}</span><span>{r.v}</span></div>
              ))}
              <div style={{ ...totalRow, fontSize: 17, fontWeight: 700, color: 'var(--heading)', borderBottom: 'none', paddingTop: 10 }}>
                <span>{t('doc.total')}</span><span>{fmt(doc.total)}</span>
              </div>
              {payable && doc.amountPaid > 0 && owed > 0 && <>
                <div style={totalRow}><span>{t('payments.paid')}</span><span>-{fmt(doc.amountPaid)}</span></div>
                <div style={{ ...totalRow, fontSize: 15, fontWeight: 700, color: 'var(--danger)', borderBottom: 'none' }}>
                  <span>{t('payments.balance')}</span><span>{fmt(owed)}</span>
                </div>
              </>}
            </div>
          </div>
        )}
      </div>

      {/* How to pay */}
      {payable && owed > 0 && company.bankName && (
        <div style={{ ...card, borderInlineStart: `4px solid ${accent}` }}>
          <p style={label}>{t('publicDoc.howToPay')}</p>
          <div style={{ fontSize: 14, color: 'var(--text)', lineHeight: 1.8 }}>
            <div><b>{t('docView.bank')}:</b> {company.bankName}</div>
            <div><b>{t('docView.account')}:</b> <span dir="ltr">{company.bankAccount}</span></div>
            {company.bankHolder && <div><b>{t('docView.name')}:</b> {company.bankHolder}</div>}
          </div>
        </div>
      )}

      {/* Notes & terms */}
      {(hasContent(doc.notes) || hasContent(doc.terms)) && (
        <div style={{ ...card, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 240px), 1fr))', gap: 24 }}>
          {hasContent(doc.notes) && <div><p style={label}>{t('docView.notes')}</p><div style={rich} className="pub-rich" dangerouslySetInnerHTML={{ __html: toHtml(doc.notes) }} /></div>}
          {hasContent(doc.terms) && <div><p style={label}>{t('docView.terms')}</p><div style={rich} className="pub-rich" dangerouslySetInnerHTML={{ __html: toHtml(doc.terms) }} /></div>}
        </div>
      )}

      {/* Quotation response */}
      {doc.clientResponse && (
        <div style={{ ...card, textAlign: 'center', fontWeight: 600, color: doc.clientResponse.action === 'accepted' ? 'var(--success)' : 'var(--danger)' }}>
          {doc.clientResponse.action === 'accepted' ? '✓ ' : '✕ '}
          {t(`publicDoc.${doc.clientResponse.action}`, { name: doc.clientResponse.name, date: fmtDate(doc.clientResponse.at) })}
        </div>
      )}
      {canRespond && (
        <div style={card}>
          <p style={{ fontSize: 16, fontWeight: 700, color: 'var(--heading)', marginBottom: 12 }}>{t('publicDoc.acceptTitle')}</p>
          <Input label={t('publicDoc.yourName')} value={name} maxLength={120} onChange={e => setName(e.target.value)} style={{ maxWidth: 360 }} />
          {error && <p style={{ fontSize: 13, color: 'var(--danger)', marginTop: 8 }}>{error}</p>}
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 14 }}>
            <Btn variant="success" size="lg" disabled={!!busy} onClick={() => respond('accept')}>✓ {t('publicDoc.accept')}</Btn>
            <Btn variant="ghost" size="lg" disabled={!!busy} onClick={() => respond('reject')}>{t('publicDoc.decline')}</Btn>
          </div>
          <p style={{ fontSize: 12, color: 'var(--text-subtle)', marginTop: 12 }}>{t('publicDoc.acceptNote')}</p>
        </div>
      )}
    </Shell>
  );
}

function Shell({ children }) {
  const { t } = useT();
  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', padding: '20px 16px 40px' }}>
      {/* Rich text brings its own lists; the global reset zeroes their indent */}
      <style>{`.pub-rich ul, .pub-rich ol { margin: 4px 0; padding-inline-start: 20px; } .pub-rich p { margin-bottom: 4px; }`}</style>
      <div style={{ maxWidth: 860, margin: '0 auto' }}>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}><LanguageSwitcher /></div>
        {children}
        <p style={{ textAlign: 'center', fontSize: 12, color: 'var(--text-subtle)', marginTop: 28 }}>
          <Link to="/" style={{ color: 'var(--text-muted)', textDecoration: 'none' }}>{t('publicDoc.poweredBy')}</Link>
        </p>
      </div>
    </div>
  );
}

const card = { background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 12, padding: '20px 24px', boxShadow: 'var(--shadow)', marginTop: 16 };
const label = { fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--text-subtle)', marginBottom: 8 };
const detail = { fontSize: 13, color: 'var(--text-muted)', marginTop: 4, lineHeight: 1.7 };
const th = { padding: '12px 16px', fontSize: 12, fontWeight: 600, color: '#fff' };
const td = { padding: '12px 16px', fontSize: 13, color: 'var(--text)', verticalAlign: 'top' };
const totalRow = { display: 'flex', justifyContent: 'space-between', fontSize: 13, color: 'var(--text-muted)', padding: '5px 0', borderBottom: '1px solid var(--border-light)' };
const rich = { fontSize: 13, color: 'var(--text)', lineHeight: 1.7 };
